<script setup lang="ts">
import { AppPermissions } from "@/shared/permissions"
import { type CSSProperties, computed, h, onBeforeMount, onMounted, ref } from "vue"
import type { DropdownOption, MenuOption } from "naive-ui"
import { NAvatar, NButton, NDropdown, NIcon, NInput, NMenu, NSwitch } from "naive-ui"
import {
	BellIcon,
	BurgerIcon,
	DropdownArrowIcon,
	IPLogoGradient,
	LogoutIcon,
	MoonIcon,
	PersonIcon,
	PlusCircleIcon,
	SearchIcon,
	SquareDashIcon,
	SunIcon
} from "@/components/icons"
import { useRoute, useRouter } from "vue-router"
import { useUIStore } from "@/store/uiStore"
import { useAuthStore } from "@/store/authStore"
import { fetchNotifications, fetchUnreadNotificationsCount } from "@/api"
import { useNotificationStore } from "@/store/notificationsStore"
import { NOTIFICATION_TYPE_MAP } from "@/shared/utils/constants"
import NotificationItem from "../parts/NotificationItem.vue"
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core"
import TheSidemenu from "@/components/layout/TheSidemenu.vue"
import LanguageSelect from "@/components/LanguageSelect.vue"
import { AuthRouteNameEnum } from "@/shared/enums/routes/auth.enum.ts"
import { CampaignRouteNameEnum } from "@/shared/enums/routes/campaign.enum.ts"
import { DashboardRouteNameEnum } from "@/shared/enums/routes/dashboard.enum.ts"
import { CompanyRouteNameEnum } from "@/shared/enums/routes/company.enum.ts"
import { MediaRouteNameEnum } from "@/shared/enums/routes/media.enum.ts"
import { ScreenRouteNameEnum } from "@/shared/enums/routes/screen.enum.ts"
import { UserRouteNameEnum } from "@/shared/enums/routes/user.enum.ts"

const breakpoints = useBreakpoints(breakpointsTailwind)
const smallerLg = breakpoints.smaller("lg")
const greaterLg = breakpoints.greater("lg")
const greaterXl = breakpoints.greater("xl")

const uiStore = useUIStore()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()

import { useI18n } from "vue-i18n"

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const isDark = ref<boolean>(uiStore.mode === "dark")
const sideMenuModel = ref<boolean>(false)

const themeChanged = (value: boolean) => {
	uiStore.$patch((store) => {
		store.mode = isDark.value ? "dark" : "light"
	})
	let theme_variables = [
		"blue-gradient-from",
		"blue-gradient-to",
		"gs-100",
		"gs-200",
		"gs-300",
		"gs-400",
		"blue",
		"green",
		"orange",
		"red",
		"red-l60",
		"bg-primary",
		"bg-secondary",
		"bg-blue",
		"bg-green",
		"bg-orange",
		"bg-red",
		"shadow-2",
		"shadow-3",
		"shadow-4",
		"inner-shadow-1",
		"inner-shadow-2"
	]

	if (isDark.value) {
		theme_variables.forEach((variable) => {
			document.documentElement.style.setProperty(
				`--${variable}`,
				getComputedStyle(document.documentElement).getPropertyValue(`--${variable}-dark`)
			)
		})
	} else {
		theme_variables.forEach((variable) => {
			document.documentElement.style.setProperty(
				`--${variable}`,
				getComputedStyle(document.documentElement).getPropertyValue(`--${variable}-light`)
			)
		})
	}
}

const railStyle = ({ focused, checked }: { focused: boolean; checked: boolean }) => {
	const style: CSSProperties = {}
	if (checked) {
		style.background = "var(--blue)"
		// style.background = 'linear-gradient(180deg, var(--blue-gradient-from) 0%,  var(--blue-gradient-to) 100%)'
		if (focused) style.boxShadow = "none"
		else style.background = "var(--blue)"
	}
	if (focused) {
		style.boxShadow = "none"
	}
	return style
}

type customOption = MenuOption & { description?: string; type?: string };

const menuOptions = computed<customOption[]>(() => [
	{
		label: t("nav.dashboard"),
		key: DashboardRouteNameEnum.dashboard,
		show: authStore.isAuthenticated
	},
	{
		label: t("nav.media"),
		key: MediaRouteNameEnum.list,
		show: authStore.can(AppPermissions.CanUploadCampaignFile)
	},
	{
		label: t("nav.screens"),
		key: ScreenRouteNameEnum.list,
		show: authStore.can(AppPermissions.CanViewOwnScreens)
	},
	{
		label: t("nav.companies"),
		key: CompanyRouteNameEnum.list,
		show: authStore.can(AppPermissions.CanViewSubordinateCompanies)
	},
	{
		label: t("nav.campaigns"),
		key: CampaignRouteNameEnum.list,
		show: authStore.can(AppPermissions.CanViewOwnCampaigns)
	}
	// {
	// 	key: "divider-1",
	// 	type: "divider",
	// 	props: {
	// 		style: {
	// 			marginLeft: "32px"
	// 		}
	// 	}
	// },
	// {
	// 	label: "Add new campaign",
	// 	key: "campaigns.locations",
	// 	icon: PlusCircleIcon,
	// 	props: {
	// 		style: {
	// 			marginLeft: -32
	// 		}
	// 	}
	// },
	// {
	// 	label: "Add new screen",
	// 	key: ScreenRouteNameEnum.create,
	// 	icon: PlusCircleIcon,
	// 	props: {
	// 		style: {
	// 			marginLeft: -32
	// 		}
	// 	}
	// }
])


const menuUpdated = (key: string, item: MenuOption) => {
	if (route.name === key) return
	router.push({ name: key })
}
const addNewCampaign = () => {
	router.push({ name: CampaignRouteNameEnum.locations })
}
const addNewScreen = () => {
	router.push({ name: ScreenRouteNameEnum.list, query: { a: "addScreen" } })
}
const addNewLocation = () => {
	router.push({ name: ScreenRouteNameEnum.create })
}
const showSideMenu = () => {
	sideMenuModel.value = true
}

const logout = (): void => {
	authStore.$patch((store) => {
		store.token = ""
		store.user = null
	})
	router.push({ name: AuthRouteNameEnum.login })
}
const handleSelectUserDropdown = (key: string | number) => {
	if (key === UserRouteNameEnum.profileGeneral) {
		router.push({ name: key })
	}
	if (key === "auth.logout") {
		logout()
	}
}


const userDropdownOptions = computed(() => [
	{
		label: t("nav.profile"),
		icon: PersonIcon,
		key: UserRouteNameEnum.profileGeneral
	},
	{
		key: "header-divider",
		type: "divider"
	},
	{
		label: t("auth.sign_out"),
		icon: LogoutIcon,
		key: "auth.logout"
	}
])

const renderNotificationsDropdownLabel = (option: DropdownOption) => {
	return h(
		h(NotificationItem, {
			title: option.title as string,
			type: NOTIFICATION_TYPE_MAP[option.type as string],
			content: option.description as string
		})
	)
}

const getNotifications = async () => {
	try {
		const response = await fetchNotifications()
		notificationStore.setNotifications(response.data)
	} catch (error) {
		console.error(error)
	}
}

const getUnreadNotificationsCount = async () => {
	try {
		const { count } = await fetchUnreadNotificationsCount()
		if (count)
			notificationStore.setUnread(count)
	} catch (error) {
		console.error(error)
	}
}

onMounted(() => {
	getNotifications()
	getUnreadNotificationsCount()
})

onBeforeMount(() => {
	themeChanged(uiStore.mode === "dark")
})
</script>

<template>
	<div class="header">
		<div class="header_left">
			<div class="header_logo">
				<IPLogoGradient />
			</div>
			<div class="header_search" v-if="authStore.can(AppPermissions.CanViewOwnCampaigns) && greaterLg">
				<n-input :placeholder="t('layouts.search_placeholder')" size="large">
					<template #prefix>
						<n-icon size="20">
							<SearchIcon />
						</n-icon>
					</template>
					<template #suffix>
						<n-icon size="20">
							<SquareDashIcon />
						</n-icon>
					</template>
				</n-input>
			</div>
			<div class="header_nav" v-if="greaterLg">
				<n-menu
					responsive
					@update:value="menuUpdated"
					:value="String(route.name)"
					mode="horizontal"
					:options="menuOptions" />
			</div>
		</div>
		<div class="header_right">
			<div class="header_campaign" v-if="greaterLg">
				<n-button
					v-if="authStore.can({ any: ['campaign.create', 'campaign.create.for-subordinate'] })"
					@click="addNewCampaign"
					class="secondary-button">
					<template #icon>
						<n-icon>
							<PlusCircleIcon />
						</n-icon>
					</template>
					{{ greaterXl ? t("campaigns.add_new_campaign") : t("campaigns.campaign_short") }}
				</n-button>
				<n-button
					v-if="authStore.can(AppPermissions.CanCreateScreen) && !authStore.needsStripeOnboarding && route.name !== ScreenRouteNameEnum.list"
					@click="addNewScreen"
					class="secondary-button">
					<template #icon>
						<n-icon>
							<PlusCircleIcon />
						</n-icon>
					</template>
					{{ greaterXl ? t("screens.add_new_screen") : t("screens.screen_short") }}
				</n-button>
				<n-button
					v-if="
						authStore.can(AppPermissions.CanCreatePlace) &&
						!authStore.needsStripeOnboarding &&
						route.name !== ScreenRouteNameEnum.list &&
						route.name !== ScreenRouteNameEnum.create
					"
					@click="addNewLocation"
					class="secondary-button">
					<template #icon>
						<n-icon>
							<PlusCircleIcon />
						</n-icon>
					</template>
					{{ greaterXl ? t("screens.add_new_location") : t("screens.location_short") }}
				</n-button>
			</div>
			<div class="header_mode-switcher" v-if="greaterLg">
				<n-switch
					:rail-style="railStyle"
					class="mode-switch"
					v-model:value="isDark"
					@update:value="themeChanged"
					size="large">
					<template #checked-icon>
						<MoonIcon />
					</template>
					<template #unchecked-icon>
						<SunIcon />
					</template>
				</n-switch>
			</div>
			<div class="header_language" v-if="greaterLg">
				<LanguageSelect />
			</div>
			<div class="header_notifications">
				<n-dropdown
					class="notification-dropdown-menu"
					trigger="click"
					:render-label="renderNotificationsDropdownLabel"
					size="large"
					:node-props="
						() => {
							return { class: 'notification-dropdown-option' };
						}
					"
					@select="router.push({ name: UserRouteNameEnum.profileNotifications })"
					:options="notificationStore.notifications">
					<n-button text style="font-size: 24px" size="medium">
						<n-icon>
							<BellIcon />
						</n-icon>
						<span v-if="notificationStore.unreadNotifications" class="counter">{{
								notificationStore.unreadNotifications
							}}</span>
					</n-button>
				</n-dropdown>
			</div>
			<div class="header_user">
				<n-dropdown
					class="user-dropdown-menu"
					trigger="click"
					:options="userDropdownOptions"
					@select="handleSelectUserDropdown">
					<n-button text :render-icon="DropdownArrowIcon" icon-placement="right"
					>
						<n-avatar round color="var(--bg-blue)" style="color: var(--blue)" :size="32"
						>JT
						</n-avatar
						>
					</n-button
					>
				</n-dropdown>

				<div class="header_user-dropdown"></div>
			</div>

			<div class="header_burger-menu" v-if="smallerLg">
				<n-button
					text circle
					size="medium"
					@click="showSideMenu"
				>
					<n-icon size="32">
						<BurgerIcon />
					</n-icon>
				</n-button>

			</div>


			<TheSidemenu :menu-options="menuOptions" v-model:show="sideMenuModel" />
		</div>
	</div>
</template>

<style lang="scss">
@use "sass:map";

.mode-switch.n-switch .n-switch__rail .n-switch__button-icon {
	padding: 0.4rem;
	color: var(--blue);
}

.header_user .n-dropdown-option-body__prefix {
	--n-prefix-color: var(--gs-300);
}

.header {
	@include flex(0, row, space-between, center);
	padding: 24px 32px;
	position: fixed;
	width: 100%;
	height: 96px;
	left: 0px;
	top: 0px;
	background: var(--bg-primary);
	z-index: 99;
	box-shadow: 0px 8px 16px 0px var(--bg-secondary);

	&_logo {
		@include flex(0, row, center, center);
		border: 1px solid var(--gs-400);
		border-radius: 0.8rem;
		padding: 1rem;
	}

	&_left {
		@include flex(3.2rem, row, start, center);
	}

	&_right {
		@include flex(1.6rem, row, start, center);
	}

	&_logo {
		@include flex(0, row, center, center);
	}

	&_search {
		@include flex(3.2rem, row, start, center);
		box-sizing: border-box;
		width: 260px;

		.n-input {
			width: 100%;
			height: 40px;
			border-radius: 8px;
			background: var(--bg-primary);
			@include font(1.6rem, 400, 2.4rem);
			color: var(--gs-200);

			&__border {
				border: 1px solid var(--gs-400);
			}

			&__placeholder {
				color: var(--gs-300);
			}

			&:hover {
				border-color: var(--gs-300);
			}

			&:focus {
				border-color: var(--blue-gradient-from);
				outline: none;
				box-shadow: none;
			}
		}
	}

	&_nav {
		.n-menu-item-content .n-menu-item-content-header {
			@include getFont("h5");
			cursor: pointer;
			color: var(--gs-100);
			transition: all 0.15s ease-in-out;

			&:hover {
				opacity: 0.8;
			}
		}
	}

	&_campaign {
		@include flex(1.6rem, row, flex-end, center);

		button {
			@include flex(3.2rem, row, start, center);
			padding: 0.8rem 1.2rem;
			gap: 0.8rem;
			height: 4rem;
			background: var(--bg-primary);
			// border: 1px solid var(--gs-400);
			border-radius: 0.8rem;
			@include font(1.6rem, 500, 2.4rem);
			color: var(--gs-100);
		}
	}

	&_user {
		@include flex(0, row, center, center);
	}

	&_burger-menu {
		@include flex(0, row, center, center);

		width: 3.2rem;
		height: 3.2rem;

		font-size: 2.4rem;
	}

	&_notifications {
		position: relative;
		@include flex(0, row, center, center);

		.n-button {
			&:not(:hover) {
				color: var(--gs-300);
			}

			&:hover {
				color: var(--blue);
			}
		}

		span.counter {
			position: absolute;
			width: 16px;
			height: 16px;
			right: -0.4rem;
			top: -0.4rem;
			background: var(--red);
			border-radius: 50%;
			@include flex(0, row, center, center);

			@include font(1rem, 600, 1.6rem);
			text-align: center;
			letter-spacing: 0.04em;
			text-transform: uppercase;
			color: #fff;
		}
	}
}

.notification-dropdown-menu.n-dropdown-menu {
	--n-divider-color: var(--gs-400) !important;
	--n-color: var(--bg-primary) !important;
	--n-padding: 2rem 1.2rem !important;
	--n-border-radius: 1.2rem !important;
}

.user-dropdown-menu.n-dropdown-menu {
	--n-divider-color: var(--gs-400) !important;
	--n-color: var(--bg-primary) !important;
	--n-prefix-color: var(--gs-300) !important;
	--n-option-text-color: var(--gs-100) !important;
	--n-option-text-color-hover: var(--blue-gradient-from) !important;
	--n-option-color-hover: var(--bg-secondary) !important;
	--n-padding: 0.8rem !important;
	--n-border-radius: 0.8rem !important;

	border-radius: 1.2rem !important;
}

.user-dropdown-menu .n-dropdown-option-body::before {
	left: 0 !important;
	right: 0 !important;
}

.user-dropdown-menu .n-dropdown-option-body__label {
	@include getFont("h6");
	line-height: var(--n-option-height) !important;
}

.notification-dropdown-option {
	width: 26rem;
	margin: 0.8rem 0;
	padding: 0.8rem;

	&:hover {
		background-color: var(--bg-secondary);
		cursor: pointer;
		border-radius: 0.8rem;
	}

	&:first-of-type {
		margin-top: -0.8rem;
	}

	&:last-of-type {
		margin-bottom: -0.8rem;
	}
}
</style>
@/components/icons
